/**
 * Tin lid designer — fullscreen modal editor shared by two pages.
 *
 * Everything happens locally in the browser (fabric.js canvas). On Save, the
 * canvas is exported as a print-resolution PNG, and where it goes depends on
 * the host page:
 *
 *   - box-personalizer product template: the PNG is stuffed into the hidden
 *     <input type="file" name="properties[Tin Lid Design]"> so it rides along
 *     with the native (non-AJAX) add-to-cart form post. Shopify stores the
 *     file and links it on the order.
 *   - box-designer page ("standalone": true in the config): there is no form
 *     and nothing is uploaded — the PNG is held in memory and the page's
 *     Output button downloads it.
 *
 * Config comes from #box-designer-config (JSON emitted by the section).
 * fabric.min.js is lazy-loaded on first open so the rest of the site pays
 * nothing for this feature.
 */
(function () {
  'use strict';

  // Self-hosted free webfonts (@font-face in snippets/box-designer-modal.liquid),
  // so the lid prints the same on every customer's device. `webfont` is the
  // @font-face name; `italic` says whether a real italic face ships (fabric's
  // italic toggle falls back to a synthesized slant otherwise).
  var FONTS = [
    { label: 'Elegant', webfont: 'Gelasio', italic: true, family: "Gelasio, Georgia, serif" },
    { label: 'Modern', webfont: 'Arimo', italic: true, family: "Arimo, Arial, sans-serif" },
    { label: 'Classic', webfont: 'BD Pagella', italic: true, family: "'BD Pagella', 'Palatino Linotype', Palatino, serif" },
    { label: 'Handwritten', webfont: 'Dancing Script', italic: false, family: "'Dancing Script', cursive" },
    { label: 'Typewriter', webfont: 'Courier Prime', italic: true, family: "'Courier Prime', 'Courier New', monospace" }
  ];
  var DEFAULT_FONT_INDEX = 1; // Modern

  var MAX_SOURCE_IMAGE_PX = 2000; // uploaded photos are downscaled to this before entering the canvas
  var MAX_UPLOAD_BYTES = 19 * 1024 * 1024; // Shopify line-item file uploads cap at 20MB
  var MIN_PHOTO_DPI = 150; // print rule of thumb: 300 ideal, below 150 visibly blurry

  // Every lid preview carries the shop's white wordmark, bottom-centred, over
  // the bare tin and over a border design alike. Production applies the real
  // mark, so it is drawn on previews only and never baked into the print file.
  // Sized and positioned in mm so it prints the same in either orientation;
  // the aspect comes from the SVG's viewBox (1024x551). LOGO_BOTTOM_MM is the
  // gap from the lid edge to the bottom of the mark — keep it at or above the
  // safe margin so the mark never lands in the die-cut zone.
  var LOGO_WIDTH_MM = 28.8;
  var LOGO_ASPECT = 551 / 1024;
  var LOGO_BOTTOM_MM = 7;

  var config = null;
  var standalone = false; // no cart form: Save hands the PNG to the Output button instead
  var els = {};
  var canvas = null; // fabric.Canvas, created on first open
  var savedState = null; // canvas JSON of the last saved design
  var fabricPromise = null;
  // The lid can be used landscape or portrait; config.lidWidthMm/lidHeightMm
  // describe landscape, portrait swaps them everywhere via lidWidthMm/lidHeightMm().
  var orientation = 'landscape';
  var savedOrientation = 'landscape';
  // The section-block id of the applied border design, or null for bare tin —
  // the print layer is transparent in that case and the metal shows through.
  var activeBgId = null;
  var savedBgId = null;
  var bgLoadToken = 0; // invalidates an in-flight image load after a background/orientation change
  var logoReady = false;
  var bgTiles = [];
  // Standalone only: the last exported PNG, waiting for the Output button.
  var downloadBlob = null;
  var downloadName = 'tin-lid-design.png';

  function lidWidthMm() {
    return orientation === 'portrait' ? config.lidHeightMm : config.lidWidthMm;
  }

  function lidHeightMm() {
    return orientation === 'portrait' ? config.lidWidthMm : config.lidHeightMm;
  }

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function init() {
    var cfgEl = document.getElementById('box-designer-config');
    if (!cfgEl) return;
    config = JSON.parse(cfgEl.textContent);
    standalone = !!config.standalone;

    els.form = qs('.box-personalizer-form');
    els.openBtn = qs('[data-bd-open]');
    els.fileInput = qs('[data-bd-file-design]');
    els.specInput = qs('[data-bd-print-spec]');
    els.thumb = qs('[data-bd-thumb]');
    els.statusText = qs('[data-bd-status-text]');
    els.designCard = qs('[data-bd-design-card]');
    els.outputBtn = qs('[data-bd-output]');
    els.outputNote = qs('[data-bd-output-note]');

    els.overlay = qs('[data-bd-overlay]');
    els.canvasHolder = qs('[data-bd-canvas-holder]');
    els.guide = qs('[data-bd-guide]');
    els.logo = qs('[data-bd-logo]');
    els.closeBtn = qs('[data-bd-close]');
    els.cancelBtn = qs('[data-bd-cancel]');
    els.saveBtn = qs('[data-bd-save]');
    els.addImageBtn = qs('[data-bd-add-image]');
    els.imageInput = qs('[data-bd-image-input]');
    els.addTextBtn = qs('[data-bd-add-text]');
    els.textControls = qs('[data-bd-text-controls]');
    els.textValue = qs('[data-bd-text-value]');
    els.fontSelect = qs('[data-bd-font]');
    els.textColor = qs('[data-bd-text-color]');
    els.boldBtn = qs('[data-bd-bold]');
    els.italicBtn = qs('[data-bd-italic]');
    els.objectControls = qs('[data-bd-object-controls]');
    els.bgField = qs('[data-bd-bg-field]');
    els.bgOpenBtn = qs('[data-bd-bg-open]');
    els.bgPopover = qs('[data-bd-bg-popover]');
    els.bgPreview = qs('[data-bd-bg-preview]');
    els.bgGrid = qs('[data-bd-bg-grid]');
    els.error = qs('[data-bd-error]');
    els.quality = qs('[data-bd-quality]');
    els.orientBtns = document.querySelectorAll('[data-bd-orient]');
    els.specSize = qs('[data-bd-spec-size]');
    els.modal = qs('.bd-modal', els.overlay);
    els.canvasFrame = els.canvasHolder ? els.canvasHolder.parentElement : null;

    if (!els.openBtn || !els.overlay) return;
    // The product template needs its cart form; the standalone page has none.
    if (!standalone && !els.form) return;

    // The theme wraps page content in .main-content[data-drawer-push], whose
    // transform (cart-drawer push effect) hijacks position:fixed — the modal
    // would anchor below the header instead of covering the viewport.
    // Reparent it to <body> so "fixed" really means the viewport.
    if (els.overlay.parentElement !== document.body) {
      document.body.appendChild(els.overlay);
    }

    // Native form post is required for file-type line item properties;
    // the form class already opts out of the theme's AJAX cart handler.
    if (els.form) els.form.setAttribute('enctype', 'multipart/form-data');

    // Loaded up front (it is a few KB of vector) so it is ready to be drawn
    // into the export canvas by the time anyone saves. crossOrigin is
    // required because theme assets come from the Shopify CDN: without it the
    // draw taints the export canvas and toDataURL throws a SecurityError.
    if (els.logo && config.logoUrl) {
      els.logo.crossOrigin = 'anonymous';
      els.logo.addEventListener('load', function () { logoReady = true; });
      els.logo.src = config.logoUrl;
    }

    FONTS.forEach(function (font, i) {
      var opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = font.label;
      opt.style.fontFamily = font.family;
      els.fontSelect.appendChild(opt);
    });

    els.openBtn.addEventListener('click', openModal);
    els.closeBtn.addEventListener('click', closeModal);
    els.cancelBtn.addEventListener('click', closeModal);
    els.saveBtn.addEventListener('click', saveDesign);
    els.addImageBtn.addEventListener('click', function () { els.imageInput.click(); });
    els.imageInput.addEventListener('change', onImagesPicked);
    els.addTextBtn.addEventListener('click', addText);
    els.overlay.addEventListener('click', function (evt) {
      if (evt.target === els.overlay) closeModal();
    });
    document.addEventListener('keydown', onKeyDown);
    if (els.form) els.form.addEventListener('submit', onFormSubmit);
    if (els.outputBtn) els.outputBtn.addEventListener('click', downloadDesign);

    els.orientBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setOrientation(btn.getAttribute('data-bd-orient'));
      });
    });

    bindObjectControls();
    bindDropZone();
    buildBackgroundPicker();
  }

  /* ------------------------------------------------------------------ *
   * Border design picker (preset images, or bare tin)
   * ------------------------------------------------------------------ */

  function backgroundPresets() {
    return (config.backgrounds || []).filter(function (bg) {
      return bg && bg.url;
    });
  }

  function availableBackgroundPresets() {
    return backgroundPresets().filter(function (bg) {
      return (bg.orientation || 'landscape') === orientation;
    });
  }

  function findBackground(id) {
    var match = null;
    backgroundPresets().forEach(function (bg) {
      if (bg.id === id) match = bg;
    });
    return match;
  }

  function buildBackgroundPicker() {
    els.bgOpenBtn.addEventListener('click', function () { toggleBgPopover(); });
    // Clicking anywhere else in the modal dismisses the popover.
    document.addEventListener('click', function (evt) {
      if (els.bgPopover.hidden) return;
      if (els.bgField.contains(evt.target)) return;
      toggleBgPopover(false);
    });

    var presets = backgroundPresets();
    // Nothing uploaded in the theme editor: the whole control has nothing to
    // offer, so it stays hidden rather than opening onto an empty grid.
    if (!presets.length) {
      els.bgField.hidden = true;
      return;
    }

    els.bgGrid.appendChild(backgroundTile(null));
    presets.forEach(function (bg) {
      els.bgGrid.appendChild(backgroundTile(bg));
    });
    refreshBackgroundAvailability();
  }

  // bg === null builds the tile that removes the border design and exposes
  // the bare metal underneath.
  function backgroundTile(bg) {
    var tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'bd-bg-tile';

    var thumb = document.createElement('span');
    thumb.className = 'bd-bg-thumb';
    var name = document.createElement('span');
    name.className = 'bd-bg-name';

    if (bg) {
      thumb.style.backgroundImage = 'url("' + bg.thumb + '")';
      name.textContent = bg.label || 'Border design';
      tile.addEventListener('click', function () { applyBgImage(bg); });
    } else {
      tile.className += ' bd-bg-tile--none';
      name.textContent = 'Bare tin';
      tile.addEventListener('click', clearPrintBackground);
    }

    tile.appendChild(thumb);
    tile.appendChild(name);
    bgTiles.push({
      id: bg ? bg.id : null,
      orientation: bg ? (bg.orientation || 'landscape') : null,
      el: tile
    });
    return tile;
  }

  // Border art is drawn for one direction only, so the grid — and the control
  // itself — only offers what fits the lid's current orientation.
  function refreshBackgroundAvailability() {
    els.bgField.hidden = availableBackgroundPresets().length === 0;
    bgTiles.forEach(function (tile) {
      tile.el.hidden = tile.orientation !== null && tile.orientation !== orientation;
    });
  }

  function toggleBgPopover(force) {
    var open = typeof force === 'boolean' ? force : els.bgPopover.hidden;
    els.bgPopover.hidden = !open;
    els.bgOpenBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function clearPrintBackground() {
    if (!canvas) return;
    bgLoadToken++;
    activeBgId = null;
    canvas.backgroundImage = null;
    canvas.setBackgroundColor('transparent', canvas.renderAll.bind(canvas));
    refreshBgUi();
  }

  function applyBgImage(bg) {
    if (!canvas) return;
    if ((bg.orientation || 'landscape') !== orientation) return;
    var requestedOrientation = orientation;
    var requestToken = ++bgLoadToken;
    // An image replaces any selected print color. Transparent parts of the
    // image reveal the physical tin rather than the previous color.
    canvas.setBackgroundColor('transparent', canvas.renderAll.bind(canvas));
    // crossOrigin is required: without it the Shopify CDN image taints the
    // canvas and the export (toDataURL) throws a SecurityError.
    fabric.Image.fromURL(bg.url, function (img, isError) {
      // A direction/background change while the CDN image was loading makes
      // this request stale; never let the old image reappear afterward.
      if (requestToken !== bgLoadToken || requestedOrientation !== orientation) return;
      if (isError || !img || !img.width) {
        showError('Could not load that background. Please pick another one.');
        return;
      }
      hideError();
      img.set({ selectable: false, evented: false });
      canvas.setBackgroundImage(img, function () {
        fitBackgroundImage();
        canvas.renderAll();
      });
      activeBgId = bg.id;
      refreshBgUi();
    }, { crossOrigin: 'anonymous' });
  }

  // Scales the background to cover the lid, cropping the overflow — the same
  // rule as CSS background-size: cover. Re-run on every canvas resize.
  function fitBackgroundImage() {
    var img = canvas && canvas.backgroundImage;
    if (!img || !img.width || !img.height) return;
    var scale = Math.max(canvas.getWidth() / img.width, canvas.getHeight() / img.height);
    img.set({
      originX: 'left',
      originY: 'top',
      scaleX: scale,
      scaleY: scale,
      left: (canvas.getWidth() - img.width * scale) / 2,
      top: (canvas.getHeight() - img.height * scale) / 2
    });
    img.setCoords();
  }

  // Logo placement as fractions of the lid, shared by the DOM preview and the
  // export draw so the two can never drift apart.
  function logoRect() {
    var width = LOGO_WIDTH_MM / lidWidthMm();
    var height = (LOGO_WIDTH_MM * LOGO_ASPECT) / lidHeightMm();
    return {
      left: (1 - width) / 2,
      top: 1 - (Math.max(LOGO_BOTTOM_MM, config.safeMarginMm) / lidHeightMm()) - height,
      width: width,
      height: height
    };
  }

  function positionLogo() {
    if (!els.logo) return;
    var rect = logoRect();
    els.logo.style.left = (rect.left * 100) + '%';
    els.logo.style.top = (rect.top * 100) + '%';
    els.logo.style.width = (rect.width * 100) + '%';
    els.logo.style.height = (rect.height * 100) + '%';
  }

  function syncLogo() {
    if (!els.logo) return;
    // A page with no logoUrl is deliberately unbranded — never show an empty
    // <img> in place of the wordmark.
    els.logo.hidden = !canvas || !config.logoUrl;
  }

  function refreshBgUi() {
    var preset = findBackground(activeBgId);
    els.bgPreview.classList.toggle('is-metallic', !preset);
    els.bgPreview.style.backgroundImage = preset ? 'url("' + preset.thumb + '")' : '';
    bgTiles.forEach(function (tile) {
      tile.el.classList.toggle('is-active', tile.id === activeBgId);
    });
    syncLogo();
  }

  // Drag-and-drop photos anywhere on the open modal; they go through the
  // same pipeline as the Upload button (downscale, center, select).
  function bindDropZone() {
    var depth = 0;

    function draggingFiles(evt) {
      return evt.dataTransfer &&
        Array.prototype.indexOf.call(evt.dataTransfer.types || [], 'Files') !== -1;
    }

    function clearHighlight() {
      depth = 0;
      els.canvasFrame.classList.remove('bd-dropping');
    }

    els.modal.addEventListener('dragenter', function (evt) {
      if (!draggingFiles(evt)) return;
      evt.preventDefault();
      depth++;
      els.canvasFrame.classList.add('bd-dropping');
    });
    els.modal.addEventListener('dragover', function (evt) {
      if (!draggingFiles(evt)) return;
      evt.preventDefault();
    });
    els.modal.addEventListener('dragleave', function () {
      if (depth > 0) depth--;
      if (depth === 0) els.canvasFrame.classList.remove('bd-dropping');
    });
    els.modal.addEventListener('drop', function (evt) {
      evt.preventDefault();
      clearHighlight();
      if (evt.dataTransfer && evt.dataTransfer.files.length) {
        addImageFiles(evt.dataTransfer.files);
      }
    });
  }

  function setOrientation(next) {
    if (next !== 'landscape' && next !== 'portrait') return;
    var changed = next !== orientation;
    var hadBackgroundImage = canvas && (activeBgId !== null || canvas.backgroundImage);
    orientation = next;
    if (changed) {
      bgLoadToken++;
      // Background images are authored for one direction only. Colors remain
      // valid in either direction, so only clear an image background here.
      if (hadBackgroundImage) clearPrintBackground();
    }
    els.orientBtns.forEach(function (btn) {
      btn.classList.toggle('is-active', btn.getAttribute('data-bd-orient') === orientation);
    });
    refreshBackgroundAvailability();
    refreshBgUi();
    if (els.specSize) els.specSize.textContent = lidWidthMm() + '×' + lidHeightMm() + ' mm';
    if (canvas) {
      applyCanvasSize();
      canvas.renderAll();
      refreshQualityWarning();
    }
  }

  /* ------------------------------------------------------------------ *
   * Modal lifecycle
   * ------------------------------------------------------------------ */

  function loadFabric() {
    if (window.fabric) return Promise.resolve();
    if (fabricPromise) return fabricPromise;
    fabricPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = config.fabricUrl;
      script.onload = resolve;
      script.onerror = function () {
        fabricPromise = null;
        reject(new Error('Could not load the design editor.'));
      };
      document.head.appendChild(script);
    });
    return fabricPromise;
  }

  // Canvas text doesn't wait for webfonts the way HTML does: fabric measures
  // and paints with whatever face is ready, and the print export would bake
  // in the fallback. Load every face up front; a face that fails to load
  // just leaves the CSS fallback in place rather than blocking the editor.
  var fontsPromise = null;
  function loadFonts() {
    if (fontsPromise) return fontsPromise;
    if (!document.fonts || !document.fonts.load) {
      fontsPromise = Promise.resolve();
      return fontsPromise;
    }
    var loads = [];
    FONTS.forEach(function (font) {
      ['400', '700'].forEach(function (weight) {
        (font.italic ? ['normal', 'italic'] : ['normal']).forEach(function (style) {
          loads.push(
            document.fonts.load(style + ' ' + weight + ' 1em "' + font.webfont + '"')
              .catch(function () {})
          );
        });
      });
    });
    fontsPromise = Promise.all(loads);
    return fontsPromise;
  }

  function openModal() {
    var originalLabel = els.openBtn.textContent;
    els.openBtn.disabled = true;
    els.openBtn.textContent = 'Loading…';

    Promise.all([loadFabric(), loadFonts()])
      .then(function () {
        els.overlay.hidden = false;
        document.body.classList.add('bd-modal-open');
        if (!canvas) setupCanvas();
        // Reopening always starts from the last *saved* design, so closing
        // without saving discards experiments (orientation included).
        if (savedState) {
          setOrientation(savedOrientation);
          canvas.loadFromJSON(savedState, function () {
            activeBgId = savedBgId;
            fitBackgroundImage();
            refreshBgUi();
            canvas.renderAll();
          });
        }
        refreshControlPanels();
      })
      .catch(function (err) {
        window.alert(err.message || 'Could not load the design editor. Please refresh and try again.');
      })
      .then(function () {
        els.openBtn.disabled = false;
        els.openBtn.textContent = originalLabel;
      });
  }

  function closeModal() {
    var active = canvas && canvas.getActiveObject();
    if (active && active.isEditing) active.exitEditing();
    toggleBgPopover(false);
    els.overlay.hidden = true;
    document.body.classList.remove('bd-modal-open');
  }

  function setupCanvas() {
    // The rotation handle otherwise shows a crosshair; use a circular-arrow
    // cursor so it reads as "rotate". Runs once, after fabric has loaded.
    var rotateSvg =
      "<svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 24 24'>" +
      "<path d='M12 5V2L7 7l5 5V8a5 5 0 1 1-5 5H4a8 8 0 1 0 8-8z' " +
      "fill='#333333' stroke='#ffffff' stroke-width='1.2'/></svg>";
    var rotateCursor = 'url("data:image/svg+xml;charset=utf-8,' +
      encodeURIComponent(rotateSvg) + '") 11 11, crosshair';
    fabric.Object.prototype.controls.mtr.cursorStyle = rotateCursor;
    if (fabric.Textbox && fabric.Textbox.prototype.controls) {
      fabric.Textbox.prototype.controls.mtr.cursorStyle = rotateCursor;
    }

    var canvasEl = qs('canvas', els.canvasHolder);
    canvas = new fabric.Canvas(canvasEl, {
      backgroundColor: 'transparent',
      preserveObjectStacking: true,
      selection: true
    });
    applyCanvasSize();

    canvas.on('selection:created', refreshControlPanels);
    canvas.on('selection:updated', refreshControlPanels);
    canvas.on('selection:cleared', refreshControlPanels);

    // Editing text inline on the canvas must keep the toolbar input in sync
    // (the other direction is handled by the input's own listener).
    canvas.on('text:changed', function (opt) {
      if (opt.target && opt.target === activeText()) {
        els.textValue.value = opt.target.text;
      }
    });

    // Chrome's address autofill ignores the autocomplete="off" fabric puts on
    // its hidden key-capture textarea and pops a suggestion dropdown over the
    // editor; "one-time-code" is a value it does respect.
    canvas.on('text:editing:entered', function (opt) {
      if (opt.target && opt.target.hiddenTextarea) {
        opt.target.hiddenTextarea.setAttribute('autocomplete', 'one-time-code');
      }
    });

    // Live print-quality feedback while photos are resized.
    canvas.on('object:scaling', refreshQualityWarning);
    canvas.on('object:modified', refreshQualityWarning);

    refreshBgUi();
  }

  // Sizes the canvas for the current orientation; called on creation and on
  // every orientation toggle. Objects keep their positions when toggling —
  // anything that ends up outside the lid is for the customer to rearrange.
  function applyCanvasSize() {
    var aspect = lidHeightMm() / lidWidthMm();
    // Measure the frame, not the holder: the holder is pinned to whatever
    // size the canvas had before (or the bare <canvas> default of 300px).
    var frame = els.canvasHolder.parentElement;
    var frameStyle = window.getComputedStyle(frame);
    var available = frame.clientWidth
      - parseFloat(frameStyle.paddingLeft || 0)
      - parseFloat(frameStyle.paddingRight || 0);
    var width = Math.min(available > 0 ? available : 560, 560);
    var height = Math.round(width * aspect);
    var maxHeight = Math.round(window.innerHeight * 0.55);
    if (height > maxHeight) {
      height = maxHeight;
      width = Math.round(height / aspect);
    }

    canvas.setDimensions({ width: width, height: height });
    // The background covers the lid, so it has to be re-fitted whenever the
    // lid changes shape (orientation toggle).
    fitBackgroundImage();
    // Pin the holder to the canvas size so the safe-area guide overlay
    // (positioned in % of the holder) lines up with the canvas exactly.
    els.canvasHolder.style.width = width + 'px';
    els.canvasHolder.style.height = height + 'px';

    // The lid has rounded corners: clip the editor preview so customers see
    // what gets cut away. The export stays a full rectangle (full bleed);
    // production die-cuts the radius.
    var radiusPx = (config.cornerRadiusMm / lidWidthMm()) * width;
    els.canvasHolder.style.borderRadius = radiusPx > 0 ? radiusPx + 'px' : '';
    els.canvasHolder.style.overflow = 'hidden';

    // Safe-area guide is a DOM overlay (pointer-events: none), so it never
    // interferes with the canvas and never leaks into the export.
    var insetX = (config.safeMarginMm / lidWidthMm()) * 100;
    var insetY = (config.safeMarginMm / lidHeightMm()) * 100;
    els.guide.style.left = insetX + '%';
    els.guide.style.right = insetX + '%';
    els.guide.style.top = insetY + '%';
    els.guide.style.bottom = insetY + '%';
    var guideRadiusPx = ((config.cornerRadiusMm - config.safeMarginMm) / lidWidthMm()) * width;
    els.guide.style.borderRadius = guideRadiusPx > 0 ? guideRadiusPx + 'px' : '';

    positionLogo();
  }

  // True while the keystroke belongs to a form field (the toolbar's text,
  // color and select controls), not to the canvas.
  function isTypingInField(target) {
    if (!target || !target.tagName) return false;
    var tag = target.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
  }

  function onKeyDown(evt) {
    if (els.overlay.hidden) return;
    if (evt.key === 'Escape') {
      // Escape peels one layer at a time: popover, then inline text editing,
      // then the modal itself.
      if (!els.bgPopover.hidden) {
        toggleBgPopover(false);
        return;
      }
      // While editing text inline, Escape exits editing; only a second
      // Escape closes the modal.
      var editing = canvas && canvas.getActiveObject();
      if (editing && editing.isEditing) {
        editing.exitEditing();
        canvas.renderAll();
        return;
      }
      closeModal();
      return;
    }
    if (evt.key === 'Delete' || evt.key === 'Backspace') {
      // Backspace inside a toolbar field is ordinary text editing — only the
      // canvas gets to read it as "delete the selected object".
      if (isTypingInField(evt.target)) return;
      var active = canvas && canvas.getActiveObject();
      if (active && !active.isEditing) {
        evt.preventDefault();
        canvas.remove(active);
        canvas.discardActiveObject();
        canvas.renderAll();
        refreshControlPanels();
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Adding content
   * ------------------------------------------------------------------ */

  function onImagesPicked() {
    var files = Array.prototype.slice.call(els.imageInput.files);
    els.imageInput.value = '';
    addImageFiles(files);
  }

  function addImageFiles(fileList) {
    Array.prototype.slice.call(fileList).forEach(function (file) {
      if (!/^image\//.test(file.type)) return;
      readImageAsDataURL(file, function (dataURL) {
        fabric.Image.fromURL(dataURL, function (img) {
          var scale = Math.min(
            (canvas.getWidth() * 0.7) / img.width,
            (canvas.getHeight() * 0.7) / img.height,
            1
          );
          img.set({
            left: canvas.getWidth() / 2,
            top: canvas.getHeight() / 2,
            originX: 'center',
            originY: 'center',
            scaleX: scale,
            scaleY: scale
          });
          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
          refreshControlPanels();
        });
      });
    });
  }

  // Downscale large photos before they enter the canvas: keeps the editor
  // responsive on phones and the exported files well under the upload cap.
  function readImageAsDataURL(file, callback) {
    var reader = new FileReader();
    reader.onload = function () {
      var img = new Image();
      img.onload = function () {
        var maxSide = Math.max(img.width, img.height);
        if (maxSide <= MAX_SOURCE_IMAGE_PX) {
          callback(reader.result);
          return;
        }
        var scale = MAX_SOURCE_IMAGE_PX / maxSide;
        var temp = document.createElement('canvas');
        temp.width = Math.round(img.width * scale);
        temp.height = Math.round(img.height * scale);
        temp.getContext('2d').drawImage(img, 0, 0, temp.width, temp.height);
        var keepAlpha = file.type === 'image/png';
        callback(keepAlpha ? temp.toDataURL('image/png') : temp.toDataURL('image/jpeg', 0.9));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }

  function addText() {
    var text = new fabric.IText('Your text here', {
      left: canvas.getWidth() / 2,
      top: canvas.getHeight() / 2,
      originX: 'center',
      originY: 'center',
      fontFamily: FONTS[DEFAULT_FONT_INDEX].family,
      fontSize: Math.round(canvas.getWidth() * 0.08),
      fill: els.textColor.value || '#4a4a4a'
    });
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    refreshControlPanels();
  }

  /* ------------------------------------------------------------------ *
   * Selected-object controls
   * ------------------------------------------------------------------ */

  function activeObject() {
    return canvas ? canvas.getActiveObject() : null;
  }

  function activeText() {
    var obj = activeObject();
    return obj && obj.type === 'i-text' ? obj : null;
  }

  // How many source pixels the photo brings per printed inch at its current
  // size on the lid. Below MIN_PHOTO_DPI the print will look blurry.
  function effectiveDpi(obj) {
    var exportWidthPx = Math.round((lidWidthMm() / 25.4) * config.dpi);
    var multiplier = exportWidthPx / canvas.getWidth();
    var dpiX = (obj.width * config.dpi) / (obj.getScaledWidth() * multiplier);
    var dpiY = (obj.height * config.dpi) / (obj.getScaledHeight() * multiplier);
    return Math.min(dpiX, dpiY);
  }

  function photoQualityStats() {
    var min = null;
    var lowCount = 0;
    canvas.getObjects().forEach(function (obj) {
      if (obj.type !== 'image') return;
      var dpi = effectiveDpi(obj);
      if (min === null || dpi < min) min = dpi;
      if (dpi < MIN_PHOTO_DPI) lowCount++;
    });
    return { min: min, lowCount: lowCount };
  }

  function refreshQualityWarning() {
    if (!els.quality || !canvas) return;
    var obj = activeObject();
    if (!obj || obj.type !== 'image') {
      els.quality.hidden = true;
      return;
    }
    var dpi = effectiveDpi(obj);
    if (dpi >= MIN_PHOTO_DPI) {
      els.quality.hidden = true;
      return;
    }
    els.quality.textContent =
      'This photo may print blurry at this size — make it smaller or use a higher-resolution photo.';
    els.quality.hidden = false;
  }

  function refreshControlPanels() {
    var obj = activeObject();
    var text = activeText();
    els.objectControls.hidden = !obj;
    els.textControls.hidden = !text;
    refreshQualityWarning();
    if (text) {
      els.textValue.value = text.text;
      els.textColor.value = rgbToHex(text.fill);
      var fontIndex = FONTS.findIndex(function (f) { return f.family === text.fontFamily; });
      els.fontSelect.value = String(fontIndex === -1 ? DEFAULT_FONT_INDEX : fontIndex);
      els.boldBtn.classList.toggle('is-active', text.fontWeight === 'bold');
      els.italicBtn.classList.toggle('is-active', text.fontStyle === 'italic');
    }
  }

  function updateActive(fn) {
    var obj = activeObject();
    if (!obj) return;
    fn(obj);
    obj.setCoords();
    canvas.renderAll();
    // Programmatic changes (the -/+ buttons) don't fire fabric's
    // object:scaling event, so refresh the print-quality warning here too.
    refreshQualityWarning();
  }

  function bindObjectControls() {
    // The toolbar input and fabric's inline editing are two editors over the
    // same text: writing through set('text') while fabric is in editing mode
    // leaves its hidden textarea holding the stale value (which resurfaces on
    // the next canvas interaction) and can strand the caret past the end of a
    // shortened text, which throws during cursor render and freezes the
    // canvas. Only one editor at a time: moving into the input ends inline
    // editing first.
    function exitInlineEditing() {
      var text = activeText();
      if (text && text.isEditing) {
        text.exitEditing();
        canvas.renderAll();
      }
    }

    els.textValue.addEventListener('focus', exitInlineEditing);
    els.textValue.addEventListener('input', function () {
      var text = activeText();
      if (!text) return;
      if (text.isEditing) text.exitEditing();
      text.set('text', els.textValue.value);
      canvas.renderAll();
    });

    els.fontSelect.addEventListener('change', function () {
      var text = activeText();
      if (!text) return;
      text.set('fontFamily', FONTS[Number(els.fontSelect.value)].family);
      canvas.renderAll();
    });

    els.textColor.addEventListener('input', function () {
      var text = activeText();
      if (!text) return;
      text.set('fill', els.textColor.value);
      canvas.renderAll();
    });

    els.boldBtn.addEventListener('click', function () {
      var text = activeText();
      if (!text) return;
      text.set('fontWeight', text.fontWeight === 'bold' ? 'normal' : 'bold');
      canvas.renderAll();
      refreshControlPanels();
    });

    els.italicBtn.addEventListener('click', function () {
      var text = activeText();
      if (!text) return;
      text.set('fontStyle', text.fontStyle === 'italic' ? 'normal' : 'italic');
      canvas.renderAll();
      refreshControlPanels();
    });

    bindControl('[data-bd-smaller]', function (obj) { obj.scale(obj.scaleX * 0.9); });
    bindControl('[data-bd-bigger]', function (obj) { obj.scale(obj.scaleX * 1.1); });
    bindControl('[data-bd-rotate-left]', function (obj) { obj.rotate(((obj.angle || 0) - 15) % 360); });
    bindControl('[data-bd-rotate-right]', function (obj) { obj.rotate(((obj.angle || 0) + 15) % 360); });
    bindControl('[data-bd-layer-up]', function (obj) { canvas.bringForward(obj); });
    bindControl('[data-bd-layer-down]', function (obj) { canvas.sendBackwards(obj); });
    bindControl('[data-bd-delete]', function (obj) {
      canvas.remove(obj);
      canvas.discardActiveObject();
      refreshControlPanels();
    });
  }

  function bindControl(selector, fn) {
    qs(selector).addEventListener('click', function () { updateActive(fn); });
  }

  /* ------------------------------------------------------------------ *
   * Save: export print PNG -> hidden file input (or the Output button)
   * ------------------------------------------------------------------ */

  // Paints the wordmark onto an exported canvas at the same fractions as the
  // editor overlay. Previews only — never call this before the print file's
  // pixels have been read.
  function stampLogo(target) {
    if (!logoReady || !els.logo || !config.logoUrl) return;
    var logo = logoRect();
    target.getContext('2d').drawImage(
      els.logo,
      Math.round(logo.left * target.width),
      Math.round(logo.top * target.height),
      Math.round(logo.width * target.width),
      Math.round(logo.height * target.height)
    );
  }

  function saveDesign() {
    if (!canvas) return;
    if (canvas.getObjects().length === 0 && !canvas.backgroundImage) {
      showError('Add a border design, an image, or some text before saving.');
      return;
    }
    hideError();
    toggleBgPopover(false);

    var active = activeObject();
    if (active && active.isEditing) active.exitEditing();
    canvas.discardActiveObject();
    canvas.renderAll();

    var quality = photoQualityStats();
    if (quality.lowCount > 0) {
      var proceed = window.confirm(
        quality.lowCount + (quality.lowCount === 1 ? ' photo' : ' photos') +
        ' may print blurry at the current size. Save anyway?'
      );
      if (!proceed) return;
    }

    var exportWidthPx = Math.round((lidWidthMm() / 25.4) * config.dpi);
    var multiplier = exportWidthPx / canvas.getWidth();
    var dataURL;
    var exportedWidth;
    var exportedHeight;
    var exportCanvas;
    try {
      // toCanvasElement gives us the exact pixel dimensions fabric produced,
      // so the print spec always matches the real file.
      exportCanvas = canvas.toCanvasElement(multiplier);
      exportedWidth = exportCanvas.width;
      exportedHeight = exportCanvas.height;
      // The exported file is the customer's artwork alone — the wordmark is
      // applied in production, so read the pixels out before the preview
      // stamp goes on below.
      dataURL = exportCanvas.toDataURL('image/png');
    } catch (err) {
      showError('Could not export the design. Please try a smaller image.');
      return;
    }

    var blob = dataURLToBlob(dataURL);
    // Only the cart route has a size ceiling; a local download does not.
    if (!standalone && blob.size > MAX_UPLOAD_BYTES) {
      showError('This design is too large to upload. Please remove or shrink an image.');
      return;
    }

    if (els.fileInput) {
      try {
        var file = new File([blob], 'tin-lid-design.png', { type: 'image/png' });
        var transfer = new DataTransfer();
        transfer.items.add(file);
        els.fileInput.files = transfer.files;
      } catch (err) {
        showError('Your browser could not attach the design. Please try a recent version of Chrome, Safari, or Firefox.');
        return;
      }
    }

    savedState = canvas.toJSON();
    savedOrientation = orientation;
    savedBgId = activeBgId;
    var printSpec =
      lidWidthMm() + 'x' + lidHeightMm() + 'mm ' + orientation + ' @ ' + config.dpi + 'DPI (' +
      exportedWidth + 'x' + exportedHeight + 'px)' +
      (config.cornerRadiusMm > 0 ? ', corner radius ' + config.cornerRadiusMm + 'mm' : '') +
      (quality.min !== null ? ', min photo ' + Math.round(quality.min) + 'DPI' : '') +
      (config.logoUrl ? ', white logo added at print (not in file)' : '');
    if (els.specInput) els.specInput.value = printSpec;

    if (standalone) {
      // Nothing is uploaded here — hold the file until Output is clicked.
      downloadBlob = blob;
      downloadName = 'tin-lid-design-' + lidWidthMm() + 'x' + lidHeightMm() + 'mm-' +
        config.dpi + 'dpi.png';
      if (els.outputBtn) els.outputBtn.disabled = false;
      if (els.outputNote) {
        els.outputNote.textContent = downloadName + ' — ' + printSpec;
        els.outputNote.hidden = false;
      }
    }

    // Off the export canvas, not the editor canvas, so the confirmation
    // thumbnail is the saved artwork at the exported crop. The wordmark goes
    // on here only, after the file has been read — previews stay branded,
    // the file does not.
    stampLogo(exportCanvas);
    els.thumb.src = thumbnailFrom(exportCanvas);
    els.thumb.hidden = false;
    els.statusText.textContent = standalone
      ? 'Design ready — click Output to download the print file.'
      : 'Design saved — it will be attached to your order.';
    els.openBtn.textContent = 'Edit your design';
    els.designCard.classList.add('bd-has-design');
    els.designCard.classList.remove('bd-design-missing');

    closeModal();
  }

  function onFormSubmit(evt) {
    if (!config.requireDesign) return;
    if (els.fileInput.files && els.fileInput.files.length > 0) return;
    evt.preventDefault();
    els.designCard.classList.add('bd-design-missing');
    els.statusText.textContent = 'Please create your design before adding to cart.';
    els.designCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Standalone page: hand the exported PNG to the browser's downloader. A
  // blob: URL (not the data: URL) keeps Safari from choking on large files.
  function downloadDesign() {
    if (!downloadBlob) return;
    var url = URL.createObjectURL(downloadBlob);
    var link = document.createElement('a');
    link.href = url;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoking immediately can cancel the download in some browsers.
    window.setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
  }

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  function thumbnailFrom(source) {
    var thumb = document.createElement('canvas');
    thumb.width = 160;
    thumb.height = Math.max(1, Math.round(source.height * (160 / source.width)));
    thumb.getContext('2d').drawImage(source, 0, 0, thumb.width, thumb.height);
    return thumb.toDataURL('image/png');
  }

  function dataURLToBlob(dataURL) {
    var parts = dataURL.split(',');
    var mime = parts[0].match(/:(.*?);/)[1];
    var binary = atob(parts[1]);
    var bytes = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }

  // <input type="color"> only accepts #rrggbb; fabric may hand back rgb() or
  // named colors depending on how the value was set.
  function rgbToHex(color) {
    if (!color || typeof color !== 'string') return '#000000';
    if (color[0] === '#') return color.length === 4
      ? '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3]
      : color;
    var match = color.match(/rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    if (!match) return '#000000';
    return '#' + [match[1], match[2], match[3]].map(function (channel) {
      return ('0' + Number(channel).toString(16)).slice(-2);
    }).join('');
  }

  function showError(message) {
    els.error.textContent = message;
    els.error.hidden = false;
  }

  function hideError() {
    els.error.hidden = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
